import { MenuItem, Select, Typography, type SelectChangeEvent } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  filterFieldOperators,
  mapDateOperator,
  type DateOperator,
  type Operator,
} from "../../../segment-operation/operators";
import type { Condition } from "../../types";

export const DateOpSelect: FC<{
  condition: Condition;
  handleFieldChange: (value: Partial<Condition>) => void;
  disabled?: boolean;
}> = ({ condition, handleFieldChange, disabled }) => {
  const { t } = useTranslation();

  return (
    <>
      <Select
        variant="outlined"
        size="small"
        label={t("Operator")}
        onChange={(event: SelectChangeEvent) => {
          handleFieldChange({
            ...condition,
            ...mapToOperatorAndCondition(event.target.value as DateOperator, condition),
          });
        }}
        labelId="operator-label"
        value={mapDateOperator(condition)}
        disabled={disabled}
      >
        {filterFieldOperators.DATE.operators.map((operatorInfo) => (
          <MenuItem key={operatorInfo.operator} value={operatorInfo.operator}>
            {operatorInfo.label(t)}
          </MenuItem>
        ))}
      </Select>
      {condition.values[0] === "TODAY" ? (
        <Typography variant="caption" color="textSecondary" fontSize={10}>
          {t("* relative to send date")}
        </Typography>
      ) : null}
    </>
  );
};

function mapToOperatorAndCondition(
  operator: Exclude<Operator, "IS NOT" | "IS TRUE" | "IS FALSE">,
  condition: Condition,
): Partial<Condition> {
  switch (operator) {
    case "IS BLANK":
      return { operation: "IS EMPTY", values: [], invert: false };
    case "IS NOT BLANK":
      return { operation: "IS EMPTY", values: [], invert: true };
    case "IS FUTURE":
      return { operation: "AFTER", values: ["TODAY"], invert: false };
    case "IS PAST":
      return { operation: "BEFORE", values: ["TODAY"], invert: false };
    case "IS FUTURE OR TODAY":
      return { operation: "BEFORE", values: ["TODAY"], invert: true };
    case "IS PAST OR TODAY":
      return { operation: "AFTER", values: ["TODAY"], invert: true };
    case "ON OR AFTER":
      return { operation: "BEFORE", values: condition.values, invert: true };
    case "ON OR BEFORE":
      return { operation: "AFTER", values: condition.values, invert: true };
    case "AFTER":
    case "BEFORE":
      return {
        operation: operator,
        values: condition.values[0] === "TODAY" ? [] : condition.values,
        invert: false,
      };
    case "ON":
    case "IS":
      return { operation: operator, values: condition.values, invert: false };
  }
}
